// PostHog session replay integration — links analytics events to replay sessions.
// Adds session replay URL to event payloads for debugging user behavior in PostHog.

import posthog from "posthog-js";
import { registerAnalyticsProvider } from "@/lib/analytics";
import { isConsentGiven } from "@/lib/consent-manager";

/**
 * Returns the current PostHog session replay URL, or null if unavailable.
 */
export function getReplayUrl(): string | null {
  try {
    const sessionId = posthog.get_session_id?.();
    if (!sessionId) return null;

    const host = import.meta.env.VITE_POSTHOG_HOST || "https://app.posthog.com";
    const projectKey = import.meta.env.VITE_POSTHOG_KEY;
    if (!projectKey) return null;

    return `${host}/replay/${sessionId}`;
  } catch {
    return null;
  }
}

/**
 * Returns the current PostHog session ID for cross-referencing.
 */
export function getSessionReplayId(): string | null {
  try {
    return posthog.get_session_id?.() ?? null;
  } catch {
    return null;
  }
}

/**
 * Registers a PostHog analytics provider that enriches events with
 * session replay metadata. This allows correlating analytics events
 * with specific moments in session recordings.
 */
export function initPostHogReplayProvider(): void {
  if (!isConsentGiven("analytics")) return;
  if (!import.meta.env.VITE_POSTHOG_KEY) return;

  registerAnalyticsProvider((name, properties) => {
    const replayId = getSessionReplayId();
    if (!replayId) return;

    // Forward enriched events to PostHog
    posthog.capture(name, {
      ...properties,
      $session_replay_id: replayId,
    });
  });
}

/**
 * Tags the current session replay with custom properties for filtering.
 * Use for high-value events like checkout abandonment, errors, etc.
 */
export function tagReplaySession(tags: Record<string, string | number | boolean>): void {
  try {
    posthog.setPersonProperties(tags);
  } catch {
    // Non-critical — fail silently
  }
}

/**
 * Marks a specific moment in the session replay timeline.
 * Shows as an annotation in the PostHog replay viewer.
 */
export function markReplayMoment(label: string, properties?: Record<string, unknown>): void {
  try {
    posthog.capture("$replay_moment", {
      label,
      ...properties,
    });
  } catch {
    // Non-critical
  }
}
